import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, ExternalLink, ArrowRight, Newspaper, Bell, Calendar, User, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { toFullUrl, isValidImageUrl } from '../utils/imageUrl';

const NavigationPopup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState(null); // { type: 'ad' | 'news', data: {...} }
  const [visible, setVisible] = useState(false);
  const [allAds, setAllAds] = useState([]);
  const [allNews, setAllNews] = useState([]);
  const previousPathRef = useRef(null); // Start as null to trigger on first load
  const popupCountRef = useRef(0);
  const hasTriggeredRef = useRef(false);

  const [contentLoaded, setContentLoaded] = useState(false);

  // Fetch all active ads and news on mount
  const fetchContent = useCallback(async () => {
    try {
      // Fetch ads
      const adsResponse = await api.get('/advertisements/active');
      const ads = adsResponse.data?.filter(ad => ad.ad_type === 'popup') || [];
      setAllAds(ads);
      
      // Fetch news
      const newsResponse = await api.get('/news');
      const news = newsResponse.data || [];
      setAllNews(news);
      
      // Mark content as loaded
      setContentLoaded(true);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      setContentLoaded(true); // Still mark as loaded to prevent infinite waiting
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Show popup on page navigation
  useEffect(() => {
    // Wait for content to be loaded
    if (!contentLoaded) {
      return;
    }
    
    const hasAds = allAds.length > 0;
    const hasNews = allNews.length > 0;
    
    if (!hasAds && !hasNews) {
      return;
    }
    
    // Check if path actually changed OR this is the first trigger after content loaded
    const isFirstLoad = previousPathRef.current === null;
    const pathChanged = location.pathname !== previousPathRef.current;
    
    if (!isFirstLoad && !pathChanged) {
      return;
    }
    
    previousPathRef.current = location.pathname;
    
    // Skip popup on certain pages
    const skipPages = ['/login', '/register', '/forgot-password', '/reset-password'];
    if (skipPages.some(page => location.pathname.startsWith(page))) {
      return;
    }
    
    // Don't show popup immediately after dismissing (1 second cooldown)
    const lastDismissed = sessionStorage.getItem('popup_last_dismissed');
    if (lastDismissed) {
      const timeSince = Date.now() - parseInt(lastDismissed);
      if (timeSince < 1000) {
        return;
      }
    }
    
    // Get rotation index
    const rotationIndex = parseInt(sessionStorage.getItem('nav_popup_index') || '0');
    
    let selectedContent = null;
    
    // Alternate between ads and news based on rotation index
    // The rotation index determines which type to show
    const showAd = rotationIndex % 2 === 0;
    
    if (showAd && hasAds) {
      // Show ad - cycle through available ads
      const adIndex = Math.floor(rotationIndex / 2) % allAds.length;
      selectedContent = { type: 'ad', data: allAds[adIndex] };
    } else if (!showAd && hasNews) {
      // Show news - cycle through available news
      const newsIndex = Math.floor((rotationIndex - 1) / 2) % allNews.length;
      selectedContent = { type: 'news', data: allNews[newsIndex] };
    } else if (hasNews) {
      // Fallback to news if no ads available but should show ad
      const newsIndex = rotationIndex % allNews.length;
      selectedContent = { type: 'news', data: allNews[newsIndex] };
    } else if (hasAds) {
      // Fallback to ad if no news available but should show news
      const adIndex = rotationIndex % allAds.length;
      selectedContent = { type: 'ad', data: allAds[adIndex] };
    }
    
    if (selectedContent) {
      // Increment rotation index for next navigation
      sessionStorage.setItem('nav_popup_index', ((rotationIndex + 1) % 100).toString());
      popupCountRef.current += 1;
      
      setContent(selectedContent);
      
      // Show popup after a brief delay for page transition
      setTimeout(() => {
        setVisible(true);
        
        // Record impression for ads
        if (selectedContent.type === 'ad') {
          recordAdImpression(selectedContent.data.id);
        }
      }, 800);
    }
  }, [location.pathname, allAds, allNews, contentLoaded]);

  const recordAdImpression = async (adId) => {
    try {
      await api.post(`/advertisements/${adId}/impression`);
    } catch (error) {
      console.error('Failed to record ad impression');
    }
  };

  const handleAdClick = async () => {
    if (content?.type === 'ad' && content.data?.link_url) {
      try {
        await api.post(`/advertisements/${content.data.id}/click`);
      } catch (error) {
        console.error('Failed to record ad click');
      }
      window.open(content.data.link_url, '_blank');
    }
    handleClose();
  };

  const handleNewsClick = () => {
    if (content?.type === 'news' && content.data?.id) {
      navigate(`/news/${content.data.id}`);
    }
    handleClose();
  };

  const handleViewAllNews = () => {
    navigate('/news');
    handleClose();
  };

  const handleClose = () => {
    setVisible(false);
    setContent(null);
    sessionStorage.setItem('popup_last_dismissed', Date.now().toString());
  };

  if (!visible || !content) {
    return null;
  }

  const isAd = content.type === 'ad';
  const data = content.data;

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[9999]"
            onClick={handleClose}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[10000] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-md"
          >
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden max-h-[85vh] overflow-y-auto">
              {/* Header - larger touch target for close button */}
              <div className={`px-3 py-3 sm:px-4 flex items-center justify-between sticky top-0 ${
                isAd 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                  : 'bg-gradient-to-r from-cyan-500 to-blue-600'
              }`}>
                <div className="flex items-center gap-2 text-white">
                  {isAd ? (
                    <>
                      <Megaphone className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span className="font-semibold text-xs sm:text-sm">Sponsored Content</span>
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 sm:h-5 sm:w-5 animate-bounce" />
                      <span className="font-semibold text-xs sm:text-sm">Latest News</span>
                    </>
                  )}
                </div>
                {/* Larger close button for mobile */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="p-2 sm:p-1.5 bg-white/20 hover:bg-white/30 active:bg-white/40 rounded-full transition-colors min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 flex items-center justify-center touch-manipulation"
                  data-testid="nav-popup-close"
                  aria-label="Close popup"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5 sm:h-5 sm:w-5 text-white" />
                </button>
              </div>
              
              {/* Image */}
              {(isAd ? data.image_url : data.featured_image) && (
                <div className="relative">
                  <img 
                    src={toFullUrl(isAd ? data.image_url : data.featured_image)} 
                    alt={data.title}
                    className="w-full h-32 sm:h-48 object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800';
                    }}
                  />
                  {isAd && data.company_name && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 sm:p-3">
                      <span className="text-xs text-white/90 bg-black/30 px-2 py-1 rounded">
                        Sponsored by {data.company_name}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Content */}
              <div className="p-3 sm:p-5">
                {/* Tags for news */}
                {!isAd && data.tags?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mb-2">
                    {data.tags.slice(0, 2).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs bg-cyan-50 text-cyan-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <h3 className="text-base sm:text-xl font-bold text-slate-900 mb-2 pr-2 leading-tight" data-testid="nav-popup-title">
                  {data.title}
                </h3>
                
                {(isAd ? data.content : data.excerpt) && (
                  <p className="text-xs sm:text-base text-slate-600 mb-3 line-clamp-2 sm:line-clamp-3">
                    {isAd ? data.content : data.excerpt}
                  </p>
                )}
                
                {/* Metadata for news */}
                {!isAd && (
                  <div className="flex items-center gap-3 sm:gap-4 text-xs text-slate-500 mb-3">
                    {data.author_name && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span className="truncate max-w-[80px] sm:max-w-none">{data.author_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(data.published_at || data.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}
                
                {/* Rotation indicator */}
                <div className="flex justify-center gap-1.5 mb-4">
                  <div className={`w-2 h-2 rounded-full ${isAd ? 'bg-amber-500' : 'bg-slate-200'}`} title="Advertisement" />
                  <div className={`w-2 h-2 rounded-full ${!isAd ? 'bg-cyan-500' : 'bg-slate-200'}`} title="News" />
                </div>
                
                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {isAd ? (
                    <>
                      {data.link_url && (
                        <Button 
                          onClick={handleAdClick}
                          className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-sm h-11 touch-manipulation"
                          data-testid="nav-popup-ad-cta"
                        >
                          Learn More
                          <ExternalLink className="h-4 w-4 ml-2" />
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                        className="w-full text-sm h-11 touch-manipulation"
                      >
                        Not Now
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        onClick={handleNewsClick}
                        className="w-full bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-sm h-11 touch-manipulation"
                        data-testid="nav-popup-news-read"
                      >
                        Read Article
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                        className="w-full text-sm h-11 touch-manipulation"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Close
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NavigationPopup;
